/**
 * @fileoverview Product Analytics - User Identification & Traits
 * @module features/analytics/identify
 * 
 * @description
 * Identify users and track their traits for personalization,
 * segmentation, and cohort analysis.
 * 
 * @useCases
 * - Link anonymous sessions to authenticated users
 * - Store user profile data for segmentation
 * - Track user lifecycle changes (signup, upgrade, churn)
 * - Enable personalized experiences
 * - Build user cohorts for analysis
 * 
 * @metrics
 * - Total identified users
 * - Anonymous to identified conversion rate
 * - Users by plan/segment
 * - User lifetime value distribution
 * - Trait distribution
 * 
 * @chartData
 * - Pie chart: Users by plan
 * - Bar chart: Users by trait value
 * - Line chart: New identifications over time
 * - Table: User segments
 * 
 * @example
 * ```typescript
 * const identify = new Identify(webId, endpoint, apiKey);
 * 
 * // Identify user on login
 * await identify.user({
 *   userId: 'user_123',
 *   traits: {
 *     email: 'user@example.com',
 *     name: 'John Doe',
 *     plan: 'premium',
 *     createdAt: '2025-01-15'
 *   }
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { getWindow, getDocument, isBrowser, type Teardown } from "../../utils/browser";
import { readGeoCookie } from "../../utils/geo";
import {
  UserTraits,
  MoneyValue,
  TimeSeriesData
} from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Configuration for identifying a user.
 */
export interface IdentifyConfig {
  /** Unique user identifier */
  userId: string;
  /** Anonymous ID to link (optional) */
  anonymousId?: string;
  /** User traits/properties */
  traits?: UserTraits;
}

/**
 * Options for {@link Identify.autoTrackForms} passive identity capture.
 */
export interface PassiveIdentityOptions {
  /** Debounce for `input` events before reading a field (ms, default 800). */
  debounceMs?: number;
  /** Extra `path` regex on which NO inputs are captured (replaces the default). */
  excludePath?: RegExp;
}

/**
 * Configuration for aliasing user IDs.
 */
export interface AliasConfig {
  /** Previous user ID */
  previousId: string;
  /** New user ID */
  userId: string;
}

/**
 * Configuration for grouping users.
 */
export interface GroupConfig {
  /** User ID */
  userId: string;
  /** Group/company ID */
  groupId: string;
  /** Group traits */
  traits?: {
    name?: string;
    industry?: string;
    employees?: number;
    plan?: string;
    [key: string]: unknown;
  };
}

/**
 * Identify response from API.
 */
export interface IdentifyResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    userId: string;
    identified: boolean;
    isNewUser: boolean;
    linkedAnonymousId?: string;
  };
  timestamp: string;
}

/**
 * User analytics data for charts.
 */
export interface UserAnalyticsData {
  /** Summary metrics */
  summary: {
    totalUsers: number;
    identifiedUsers: number;
    anonymousUsers: number;
    newUsersThisPeriod: number;
    identificationRate: number;
  };
  /** Users by plan/segment */
  byPlan: Array<{
    plan: string;
    count: number;
    percentage: number;
    mrr?: MoneyValue;
  }>;
  /** New users time series */
  newUsersTimeSeries: TimeSeriesData;
  /** User lifecycle distribution */
  byLifecycleStage: Array<{
    stage: string;
    count: number;
    percentage: number;
  }>;
}

// ============================================================================
// IDENTIFY CLASS
// ============================================================================

/**
 * Identify - User identification and trait management.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides user identification capabilities including:
 * - User identification on login/signup
 * - Anonymous to identified user linking
 * - User trait/property storage
 * - User aliasing for ID changes
 * - Group/company association
 * 
 * @example
 * ```typescript
 * const identify = new Identify(webId, endpoint, apiKey);
 * 
 * // On user signup
 * await identify.user({
 *   userId: 'user_123',
 *   anonymousId: getAnonymousId(),
 *   traits: {
 *     email: 'user@example.com',
 *     name: 'Jane Smith',
 *     plan: 'starter',
 *     createdAt: new Date().toISOString()
 *   }
 * });
 * 
 * // Associate user with company
 * await identify.group({
 *   userId: 'user_123',
 *   groupId: 'company_456',
 *   traits: { name: 'Acme Inc', employees: 50 }
 * });
 * ```
 */
export class Identify extends FlowGridTransport {
  /**
   * Identifies a user with traits.
   * 
   * @param config - Identification configuration
   * @returns Promise resolving to identify response
   * 
   * @example
   * ```typescript
   * // Basic identification
   * await identify.user({
   *   userId: 'user_123',
   *   traits: { email: 'user@example.com' }
   * });
   * 
   * // With anonymous ID linking
   * await identify.user({
   *   userId: 'user_123',
   *   anonymousId: localStorage.getItem('anonId'),
   *   traits: {
   *     name: 'John Doe',
   *     plan: 'premium',
   *     ltv: { amount: 29900, currency: 'USD' }
   *   }
   * });
   * ```
   */
  async user(config: IdentifyConfig): Promise<IdentifyResponse> {
    // Carry the visitor's location so an identify is enough to place them on
    // the map. Without it, a visitor identified before (or instead of) any
    // location event had no country anywhere in the system — permanently.
    // Synchronous cookie read: identify never waits on a geo lookup.
    const geo = readGeoCookie();
    return await this.init("identify_user", {
      type: "identify",
      userId: config.userId,
      anonymousId: config.anonymousId,
      traits: config.traits || {},
      ...(geo ? { geo } : {}),
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Updates traits for an existing user.
   * 
   * @param userId - User identifier
   * @param traits - Traits to update/add
   * @returns Promise resolving to identify response
   * 
   * @example
   * ```typescript
   * // Update user plan after upgrade
   * await identify.updateTraits('user_123', {
   *   plan: 'enterprise',
   *   ltv: { amount: 99900, currency: 'USD' }
   * });
   * ```
   */
  async updateTraits(userId: string, traits: UserTraits): Promise<IdentifyResponse> {
    return await this.init("update_traits", {
      type: "update_traits",
      userId,
      traits,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Creates an alias between two user IDs.
   * 
   * @param config - Alias configuration
   * @returns Promise resolving to alias response
   * 
   * @example
   * ```typescript
   * // When user ID changes (e.g., after migration)
   * await identify.alias({
   *   previousId: 'old_user_id',
   *   userId: 'new_user_id'
   * });
   * ```
   */
  async alias(config: AliasConfig): Promise<IdentifyResponse> {
    return await this.init("alias_user", {
      type: "alias",
      previousId: config.previousId,
      userId: config.userId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Associates a user with a group/company.
   * 
   * @param config - Group configuration
   * @returns Promise resolving to group response
   * 
   * @example
   * ```typescript
   * await identify.group({
   *   userId: 'user_123',
   *   groupId: 'company_456',
   *   traits: {
   *     name: 'Acme Corporation',
   *     industry: 'Technology',
   *     employees: 500,
   *     plan: 'enterprise'
   *   }
   * });
   * ```
   */
  async group(config: GroupConfig): Promise<IdentifyResponse> {
    return await this.init("group_user", {
      type: "group",
      userId: config.userId,
      groupId: config.groupId,
      traits: config.traits || {},
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Resets user identity (logout).
   * 
   * @param userId - User ID to reset
   * @returns Promise resolving to reset response
   */
  async reset(userId: string): Promise<IdentifyResponse> {
    return await this.init("reset_identity", {
      type: "reset",
      userId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Passive identity capture — the browser-side form-field tracker (parity with
   * `flowgrid.js`'s `setupPassiveIdentityCapture`). Watches inputs (initial scan
   * + a MutationObserver for fields added by modals/SPAs), classifies each by
   * name/id/placeholder/autocomplete, hashes PII (SHA-256) before it leaves the
   * browser, and posts the captured identity to the flowgrid-core identity store
   * (`POST /api/v1/identity/identify`) so the dashboard can map the anonymous
   * `visitor_id` to a person — and, with a CRM connected, to a known customer.
   *
   * Privacy: requires `analytics` consent; never attaches to password/card/cvv/
   * ssn/pin fields or anything marked `data-analytics="ignore"` / `.no-track`;
   * skips sensitive paths (checkout/account-settings/admin). Off by default in
   * {@link FlowGrid.autoTrack} — enable with `autoTrack({ passiveIdentity: true })`.
   *
   * @returns a teardown that detaches every listener (no-op on the server).
   */
  autoTrackForms(options: PassiveIdentityOptions = {}): Teardown {
    if (!isBrowser()) return () => { /* no-op on the server */ };
    if (!FlowGridTransport.hasConsent("analytics")) return () => { /* consent not granted */ };

    const win = getWindow()!;
    const doc = getDocument()!;
    const debounceMs = options.debounceMs ?? 800;

    const EXCLUDED_PATH_RE = options.excludePath ?? /\/(checkout|account\/settings|admin)(\/|$)/i;
    const BLOCKED_RE = /(password|cardnumber|creditcard|\bcard\b|cvv|cvc|ssn|\bpin\b|securitycode)/i;
    const SKIP_RE = /(message|comment|note|feedback|search|query)/i;

    type FieldRule = { re: RegExp; trait: string; pii: boolean; identify: boolean };
    const FIELD_MAP: FieldRule[] = [
      { re: /(email|emailaddress)/, trait: "email", pii: true, identify: true },
      { re: /(phone|tel|mobile|cell)/, trait: "phone", pii: true, identify: false },
      { re: /(firstname|fname|givenname)/, trait: "firstName", pii: false, identify: false },
      { re: /(lastname|lname|surname|familyname)/, trait: "lastName", pii: false, identify: false },
      { re: /(company|organi[sz]ation|businessname)/, trait: "company", pii: false, identify: false },
      { re: /(streetaddress|addressline|^address$|^street$)/, trait: "address", pii: false, identify: false },
      { re: /(^city$|town)/, trait: "city", pii: false, identify: false },
      { re: /(postcode|postalcode|zipcode|^zip$)/, trait: "postcode", pii: false, identify: false },
      // A single full-name / username field maps to firstName; listed last so
      // dedicated first/last-name fields win when both exist.
      { re: /(fullname|^name$|username|yourname|contactname|displayname)/, trait: "firstName", pii: false, identify: false },
    ];

    const captured: Record<string, string> = {};
    const seen = new WeakSet<Element>();
    const debounceTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>();
    const teardowns: Array<() => void> = [];

    const fieldEl = (el: Element) => el as HTMLInputElement;

    const normalize = (el: Element): string =>
      [fieldEl(el).name, el.id, el.getAttribute("placeholder"), el.getAttribute("autocomplete")]
        .filter(Boolean).join(" ").toLowerCase().replace(/[\s_-]/g, "");

    const isBlocked = (el: Element, key: string): boolean => {
      const input = fieldEl(el);
      if (input.type === "password" || input.type === "hidden") return true;
      if (el.getAttribute("data-analytics") === "ignore") return true;
      if (el.classList && el.classList.contains("no-track")) return true;
      return BLOCKED_RE.test(key);
    };

    const classify = (el: Element): FieldRule | { blocked: true } | null => {
      const key = normalize(el);
      if (!key) return null;
      if (isBlocked(el, key)) return { blocked: true };
      if (SKIP_RE.test(key)) return null;
      return FIELD_MAP.find((f) => f.re.test(key)) ?? null;
    };

    const sha256hex = async (value: string): Promise<string | null> => {
      try {
        const enc = new TextEncoder().encode(String(value).trim().toLowerCase());
        const buf = await win.crypto.subtle.digest("SHA-256", enc);
        return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
      } catch {
        return null;
      }
    };

    const resolveVisitorId = (): string =>
      (this.identityManager ? this.identityManager.getVisitorId() : this.visitorId) || "";
    const resolveSessionId = (): string | null =>
      this.identityManager ? this.identityManager.getSessionId() : null;

    // Posts the captured identity to the flowgrid-core identity store. Raw email
    // stays server-side (CRM enrichment + dashboard display); the analytics
    // event store only ever sees hashes (the `field_captured` mirror below).
    const sendIdentity = () => {
      const visitorId = resolveVisitorId();
      if (!this.webId || !visitorId) return;
      if (!captured.email && !captured.emailHash && !captured.firstName && !captured.lastName && !captured.phone) return;

      const traits = {
        email: captured.email ?? null,
        email_hash: captured.emailHash ?? null,
        // Raw phone is only populated on an explicit form submit (consent);
        // passive blur/input keeps phone hash-only.
        phone: captured.phone ?? null,
        phone_hash: captured.phoneHash ?? null,
        first_name: captured.firstName ?? null,
        last_name: captured.lastName ?? null,
        company: captured.company ?? null,
        address: captured.address ?? null,
        city: captured.city ?? null,
        postcode: captured.postcode ?? null,
      };
      const url = `${this.endpoint}/api/v1/identity/identify`;
      const payload = JSON.stringify({
        web_id: this.webId,
        visitor_id: visitorId,
        session_id: resolveSessionId(),
        path_name: win.location.pathname,
        traits,
      });
      try {
        if (typeof navigator !== "undefined" && navigator.sendBeacon &&
          navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }))) {
          // sent via beacon
        } else {
          void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => { /* swallow */ });
        }
      } catch { /* swallow */ }
    };

    const captureField = (el: Element) => {
      const cls = classify(el);
      if (!cls || "blocked" in cls) return;
      const value = (fieldEl(el).value || "").trim();
      if (!value) return;

      if (cls.pii) {
        const toHash = cls.trait === "phone" ? value.replace(/[^\d+]/g, "") : value;
        void sha256hex(toHash).then((hash) => {
          if (cls.trait === "email") { captured.email = value; if (hash) captured.emailHash = hash; }
          else if (cls.trait === "phone" && hash) { captured.phoneHash = hash; }
          if (cls.identify || captured.email) sendIdentity();
        });
      } else {
        captured[cls.trait] = value;
        if (captured.email) sendIdentity();
      }
    };

    const attach = (el: Element) => {
      if (seen.has(el)) return;
      const cls = classify(el);
      if (!cls || "blocked" in cls) return; // never listen to blocked / unknown fields
      seen.add(el);
      const onBlur = () => captureField(el);
      const onInput = () => {
        clearTimeout(debounceTimers.get(el));
        debounceTimers.set(el, setTimeout(() => captureField(el), debounceMs));
      };
      el.addEventListener("blur", onBlur, { passive: true } as AddEventListenerOptions);
      el.addEventListener("input", onInput, { passive: true } as AddEventListenerOptions);
      teardowns.push(() => {
        el.removeEventListener("blur", onBlur);
        el.removeEventListener("input", onInput);
      });
    };

    const scanPage = () => {
      if (EXCLUDED_PATH_RE.test(win.location.pathname)) return;
      doc.querySelectorAll("input, textarea, select").forEach(attach);
    };

    // Explicit submit is treated as consent — capture the RAW phone (the one
    // identity field passive tracking keeps hash-only) so the CRM cascade can
    // match by phone, then flush. All other fields are already captured passively.
    const onSubmit = (e: Event) => {
      const form = e.target as HTMLElement | null;
      if (!form || form.tagName !== "FORM") return;
      if (EXCLUDED_PATH_RE.test(win.location.pathname)) return;
      let phoneOnSubmit = false;
      (form as HTMLFormElement).querySelectorAll("input, textarea, select").forEach((el) => {
        const cls = classify(el);
        if (!cls || "blocked" in cls || cls.trait !== "phone") return;
        const raw = (fieldEl(el).value || "").trim();
        if (raw) { captured.phone = raw; phoneOnSubmit = true; }
      });
      if (phoneOnSubmit) sendIdentity();
    };
    doc.addEventListener("submit", onSubmit, true);
    teardowns.push(() => doc.removeEventListener("submit", onSubmit, true));

    scanPage();
    let mo: MutationObserver | null = null;
    try {
      mo = new MutationObserver(() => scanPage());
      if (doc.body) mo.observe(doc.body, { childList: true, subtree: true });
    } catch { /* MutationObserver unavailable */ }

    return () => {
      if (mo) try { mo.disconnect(); } catch { /* noop */ }
      for (const t of teardowns) { try { t(); } catch { /* noop */ } }
    };
  }

}

export default Identify;
